import {
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	ContextMenu,
	Pagination,
	useConfirmationDialog,
	TableView,
	SearchInputForm,
	computeContextMenuTitle,
	useContextMenu,
	SearchPlaceholder,
	ToolbarAction,
	FloatingActionButton,
	ToolbarRegularAction,
	TableViewNoResultConfig
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../../SettingsPage";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useDeepComparison,
	useSelection,
	useSortHandler,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { contains } from "client/Data/searching";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	AutotaskConnectionListItemModel,
	deleteMultiple,
	list
} from "client/Api/AutotaskConfiguration/autotaskConfiguration";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { DataSourceSortDirection, DataSourceSortableState } from "client/Data/sorting";
import { AutotaskConnectionDialog } from "./AutotaskConnectionDialog";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { resolveFormattedTextString } from "client/Misc/utils";

export const AutotaskConnectionPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<readonly AutotaskConnectionListItemModel[]>([]);
	const [selectedItems, setSelectedItems] = useState<ReadonlyArray<AutotaskConnectionListItemModel>>([]);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [sortingState, setSortingState] = useState<DataSourceSortableState<AutotaskConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);

	const [configureDialogState, setConfigureDialogState] = useState<ConfigureConnectionState>({
		isVisible: false
	});

	const tableRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const alertManager: AlertManager = useAlerts();

	useActiveSettingsMenuItem(SettingsPageKind.Autotask);

	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const afterSaveHandler = useCallback(
		(item: AutotaskConnectionListItemModel): void => {
			setAllItems(allItems => {
				const existingItemIndex: number = allItems.findIndex(i => i.id === item.id);
				const copy: AutotaskConnectionListItemModel[] = [...allItems];

				if (existingItemIndex === -1) {
					copy.push(item);
				} else {
					copy.splice(existingItemIndex, 1, item);
				}

				return copy;
			});
		},
		[]
	);

	const deleteHandler = useCallback(
		async (items: readonly AutotaskConnectionListItemModel[]): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-autotask-connection-confirmation-dialog",
				title: items.length === 1
					? localize("_AreYouSureYouWantToDeleteThisConnection")
					: localize("_AreYouSureYouWantToDeleteTheseConnections"),
				confirmText: localize("_Delete")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteMultiple(
					items.map(x => x.id),
					globalCancellation
				);

				setAllItems(
					(x) => x.filter(
						a => !items.some(di => a.id === di.id)
					)
				);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				setIsBusy(false);
				alertManager.showError(localize("_ErrorDeletingConnections"));
			}
		},
		[
			alertManager,
			globalCancellation,
			openConfirmationDialog
		]
	);

	const editConnectionHandler = (item: AutotaskConnectionListItemModel) => {
		setConfigureDialogState({
			isVisible: true,
			connectionId: item.id
		});
	};

	const { sortingFor } = useSortHandler<AutotaskConnectionListItemModel>(sortingState, setSortingState);
	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm]);

	useEffect(
		(): void => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	useEffect(
		() => {
			tableRef.current
				?.scrollTo(0, 0);
		},
		[
			sortingState,
			searchTerm,
			currentPageNumber
		]
	);

	const { currentItems, pagination } = useAutotaskConnectionDataSource(
		allItems,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 25
			},
			searchTerm
		}
	);

	const { multiSelection, selectionFor } = useSelection(
		currentItems,
		"id",
		{
			isMultiSelect: true,
			selectedItems,
			setSelectedItems,
			hasMemorization: false
		}
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const selectionActions: readonly ConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly ConnectionAction[] = useMemo(
		() => listContextualActions({
			editHandler: (item) => editConnectionHandler(item),
			deleteHandler
		}),
		[deleteHandler]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ConnectionActionContext> => {
			return getMainAction(
				() => setConfigureDialogState({isVisible: true})
			);
		},
		[]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	useEffect(
		(): (() => void) => {
			async function load(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const items: readonly AutotaskConnectionListItemModel[] = await list(signal)
						.catch(handlePageDataLoadingError);

					setAllItems(items);
					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(false);
				}
			}

			const cancellation: AbortController = new AbortController();
			load(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Autotask")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_Autotask")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={actionContext}
				title={localize("_AutotaskConnections")}
				hasSelection={hasSelection}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInputForm
							onChange={setSearchTerm}
							trackingId="autotask-search-input"
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[localize("_Name")]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								}
							]}
							trackingId="autotask-connections"
							ref={tableRef}
							noResultConfig={noResultConfig}
						>
							{item => (
								<TableView.Row
									selectable={selectionFor(item)}
									onExecute={() => editConnectionHandler(item)}
									onContextMenu={contextMenuHandler}
									trackingId={item.id.toString()}
								>
									<TableView.Cell>{item.name}</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{ contextMenuProps && (
							<ContextMenu<ConnectionActionContext>
								{... contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="autotask-connection-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{configureDialogState.isVisible && (
				<AutotaskConnectionDialog
					connectionId={configureDialogState.connectionId}
					onClose={() => setConfigureDialogState({isVisible: false})}
					onAfterSave={afterSaveHandler}
				/>
			)}
			{confirmationDialog}
		</>
	);
};
AutotaskConnectionPage.displayName = "AutotaskConnectionPage";

type ConfigureConnectionState = {
	isVisible: boolean;
	connectionId?: number;
};

function searchMatchFunction(
	item: AutotaskConnectionListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	if (contains(name, normalizedNotEmptySearchTerm)) {
		return true;
	}

	return false;
}

type ConnectionActionContext = {
	selection: readonly AutotaskConnectionListItemModel[];
	isMobile: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

type ContextualActionsHandlers = {
	editHandler(item: AutotaskConnectionListItemModel): void;
	deleteHandler(items: readonly AutotaskConnectionListItemModel[]): void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly ConnectionAction[] {
	const {
		editHandler,
		deleteHandler
	} = handlers;

	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => editHandler(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => deleteHandler(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

const useAutotaskConnectionDataSource =
	createLocalDataSource<AutotaskConnectionListItemModel>()
		.setConfiguration({
			search: {
				doesMatchItem: searchMatchFunction,
			},
			hasPagination: true
		})
		.getHook();

export const autotaskConfigurationRouting = {
	page: "/AutotaskConfiguration"
};